import React, { useMemo, useState } from "react";
import { arrayUnion, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";
import {
  cleanText,
  collectProjectDocumentRecords,
  dateInputValue,
  findProjectDocument,
  firstText,
  getPacsIdentity,
  getWorkStartState,
  safeFilePart,
} from "./pacsWorkflowUtils";
import {
  buildPackageIndexHtml,
  buildSubsidyFirstInstallmentHtml,
  buildSubsidyUndertakingHtml,
} from "./pacsOfficialTemplates";
import {
  downloadOfficialPdf,
  officialHtmlToPdfBlob,
  openOfficialPreview,
} from "./officialPdf";
import { PACS_DOCUMENT_TYPES } from "./generatePacsDocuments";
import {
  PACS_FOUNDATION_DOCUMENTS,
  REQUIRED_SIGNED_PACS_DOCUMENT_COUNT,
  technicalDocumentState,
} from "./pacsAutomationConfig";
import { createZipBlob, downloadBlob, fetchRemoteBlob } from "./simpleZip";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const REQUIRED_PACS_DOCUMENT_COUNT =
  Number(REQUIRED_SIGNED_PACS_DOCUMENT_COUNT) || 5;

const SIGNED_APPLICATION = {
  id: "SIGNED_APPLICATION",
  title: "Signed First Instalment Application Form",
};

const CHECKLIST = [
  { id: "RESOLUTION", title: "Managing Committee / General Body Resolution", terms: ["COMMITTEE_RESOLUTION", "RESOLUTION"] },
  { id: "LOAN_SANCTION", title: "Bank Loan Sanction Letter", terms: ["LOAN SANCTION", "SANCTION LETTER"] },
  { id: "FIRST_DISBURSEMENT_PROOF", title: "First Loan Disbursement Proof", terms: ["FIRST DISBURSEMENT", "DISBURSEMENT PROOF"] },
  { id: "LAND_LEASE", title: "Land Document + Registered Lease (for Lease Land)", terms: ["SIGNED_LEASE", "LAND_RECORD"] },
  { id: "NA_PLAN", title: "NA Order and Approved Plan", terms: ["NA ORDER", "PLAN APPROVAL", "APPROVED PLAN"] },
  { id: "DPR_DRAWINGS", title: "DPR, Technical Estimate and Certified Drawings", terms: ["DPR", "TECHNICAL ESTIMATE", "DRAWING"] },
  { id: "AUDIT", title: "Latest Audit Report and Audit Memo", terms: ["AUDIT"] },
  { id: "REGISTRAR_CERTIFICATE", title: "District Registrar No-Proceedings Certificate", terms: ["NO PROCEEDINGS", "REGISTRAR CERTIFICATE"] },
  { id: "NO_DOUBLE_SUBSIDY", title: "Signed No-Double-Subsidy Undertaking", terms: ["DOUBLE SUBSIDY"] },
  { id: "INSURANCE_UNDERTAKING", title: "Signed Godown Insurance Undertaking", terms: ["INSURANCE UNDERTAKING"] },
];

const cleanFileName = (value) => String(value || "document").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 120);

const officialPacsDocuments = (project = {}) =>
  PACS_DOCUMENT_TYPES.map(
    (item) =>
      project.generatedDocuments?.[item.id] ||
      project.signedPacsDocuments?.[item.id],
  ).filter((item) => item?.url);

const makeForm = (profile, project) => {
  const identity = getPacsIdentity(profile, project);
  const saved = project.subsidyFirstInstallment || {};
  return {
    schemeYear: saved.schemeYear || "2026-27",
    societyType: saved.societyType || project.societyType || "PACS",
    executingAgency: saved.executingAgency || project.contractorName || project.selectedContractorName || "",
    loanSanctionNumber: saved.loanSanctionNumber || project.loanSanctionNumber || "",
    loanSanctionDate: dateInputValue(saved.loanSanctionDate || project.loanSanctionDate),
    totalSanctionedLoan: saved.totalSanctionedLoan || project.sanctionedLoanAmount || project.loanSanctionedAmount || "",
    firstDisbursedAmount: saved.firstDisbursedAmount || project.firstLoanDisbursedAmount || "",
    firstDisbursementDate: dateInputValue(saved.firstDisbursementDate || project.firstLoanDisbursementDate),
    loanAccountNumber: saved.loanAccountNumber || project.loanAccountNumber || identity.bankAccountNumber || "",
    otherCentralSchemeBenefit: saved.otherCentralSchemeBenefit || "NO",
    otherSchemeName: saved.otherSchemeName || "",
    otherSubsidyAmount: saved.otherSubsidyAmount || "",
    otherSchemeUnitCost: saved.otherSchemeUnitCost || "",
    declarationDate: dateInputValue(saved.declarationDate) || new Date().toISOString().slice(0, 10),
    declarationPlace: saved.declarationPlace || identity.village || "",
    remarks: saved.remarks || "",
  };
};

export default function PacsSubsidyFirstInstallment({
  profile = {},
  project = {},
  onSaved,
}) {
  const identity = useMemo(() => getPacsIdentity(profile, project), [profile, project]);
  const work = useMemo(() => getWorkStartState(project), [project]);
  const records = useMemo(() => collectProjectDocumentRecords(project), [project]);
  const pmcTechnicalState = useMemo(
    () => technicalDocumentState(project),
    [project],
  );
  const reusableFoundation = useMemo(
    () =>
      PACS_FOUNDATION_DOCUMENTS.map((item) => ({
        ...item,
        record: findProjectDocument(records, ...item.terms),
        available: Boolean(findProjectDocument(records, ...item.terms)?.url),
      })),
    [records],
  );
  const [form, setForm] = useState(() => makeForm(profile, project));
  const [documents, setDocuments] = useState(project.subsidyFirstInstallment?.documents || {});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const checklistState = useMemo(() => CHECKLIST.map((group) => {
    const uploaded = documents[group.id];
    const linked = findProjectDocument(records, ...group.terms);
    let special = null;
    if (group.id === "RESOLUTION") {
      const combinedOfficial = project.registrarFileDocuments?.RESOLUTIONS;
      const officialDocuments = officialPacsDocuments(project);
      if (
        combinedOfficial?.url &&
        combinedOfficial.status !== "DELETED"
      ) {
        special = combinedOfficial;
      } else if (officialDocuments.length === REQUIRED_PACS_DOCUMENT_COUNT) {
        special = {
          title: "All five system-generated signed-and-stamped PACS PDFs",
          url: "MULTIPLE_RESOLUTIONS",
        };
      }
    }
    if (group.id === "LAND_LEASE") {
      const signedLeaseUrl = project.landLeaseDocuments?.SIGNED_LEASE?.url;
      const landDocumentUrl = project.landLeaseDocuments?.LAND_RECORD?.url;
      const leaseMode = cleanText(identity.landOwnershipType).toUpperCase().includes("LEASE");
      const complete = leaseMode
        ? Boolean(signedLeaseUrl && landDocumentUrl)
        : Boolean(landDocumentUrl);
      if (complete) {
        special = {
          title: leaseMode
            ? "Executed registered lease agreement and land document"
            : "Land document",
          url: signedLeaseUrl || landDocumentUrl,
        };
      }
    }
    if (group.id === "DPR_DRAWINGS") {
      const required = pmcTechnicalState.filter(
        (item) => item.requiredForSubsidy,
      );
      if (required.every((item) => item.available))
        special = {
          title: "PMC Survey, Soil Test, Design, Estimate and DPR complete",
          url: "MULTIPLE_PMC_TECHNICAL",
        };
    }
    const allowedLinked = ["LAND_LEASE", "DPR_DRAWINGS"].includes(group.id)
      ? null
      : linked;
    return { ...group, record: uploaded?.url ? uploaded : special || allowedLinked, available: Boolean(uploaded?.url || special?.url || allowedLinked?.url) };
  }), [documents, identity.landOwnershipType, pmcTechnicalState, project, records]);

  const firstDisbursementRecord = checklistState.find((item) => item.id === "FIRST_DISBURSEMENT_PROOF");
  const signedApplicationRecord = documents[SIGNED_APPLICATION.id] || null;
  const stageReasons = [];
  if (!work.started) stageReasons.push("PMC-confirmed work start is pending.");
  const missingPmcTechnical = pmcTechnicalState.filter(
    (item) => item.requiredForSubsidy && !item.available,
  );
  if (missingPmcTechnical.length)
    stageReasons.push(
      `PMC technical file pending: ${missingPmcTechnical
        .map((item) => item.shortTitle)
        .join(", ")}.`,
    );
  if (!Number(form.firstDisbursedAmount)) stageReasons.push("First loan disbursement amount is pending.");
  if (!firstDisbursementRecord?.available) stageReasons.push("First loan disbursement proof is pending.");
  const stageUnlocked = stageReasons.length === 0;
  const missingDocuments = [
    ...(signedApplicationRecord?.url ? [] : [SIGNED_APPLICATION]),
    ...checklistState.filter((item) => !item.available),
  ];
  const packageReady = stageUnlocked && missingDocuments.length === 0;

  const data = {
    ...identity,
    ...form,
    workStartDate: work.workStartDate,
    estimatedProjectCost: identity.estimatedProjectCost,
    capacityMt: identity.capacityMt,
    landOwnershipType: identity.landOwnershipType,
    leaseYears: identity.leaseYears,
    surveyNo: identity.surveyNo,
    khataNo: identity.khataNo,
    bankName: identity.bankName,
    bankBranch: identity.bankBranch,
  };

  const show = (text, type = "info") => setMessage({ text, type });
  const set = (name, value) => {
    setForm((old) => ({ ...old, [name]: value }));
    if (message.text) setMessage({ type: "", text: "" });
  };

  const validateDetails = () => {
    if (!work.started) return "Subsidy First Instalment is available only after PMC-confirmed work start.";
    if (!form.loanSanctionNumber || !form.loanSanctionDate) return "Enter bank loan sanction number and date.";
    if (!Number(form.totalSanctionedLoan)) return "Enter total sanctioned bank loan amount.";
    if (!Number(form.firstDisbursedAmount)) return "Enter first loan disbursement amount.";
    if (!form.firstDisbursementDate) return "Enter first loan disbursement date.";
    if (!form.loanAccountNumber) return "Enter loan / cash-credit account number.";
    if (identity.capacityMt < 500 || identity.capacityMt > 5000)
      return "Subsidy form capacity must be between 500 and 5000 MT.";
    if (identity.landOwnershipType.includes("LEASE") && Number(identity.leaseYears) < 20)
      return "Registered lease period must be at least 20 years.";
    if (form.otherCentralSchemeBenefit === "YES" && !form.otherSchemeName)
      return "Enter the other Central Government scheme details.";
    return "";
  };

  const saveDetails = async () => {
    const error = validateDetails();
    if (error) return show(error, "error");
    const user = auth.currentUser;
    if (!user || !project.id) return show("Application/login is unavailable.", "error");
    try {
      setBusy("save");
      const current = project.subsidyFirstInstallment || {};
      const payload = {
        ...current,
        ...form,
        totalSanctionedLoan: Number(form.totalSanctionedLoan || 0),
        firstDisbursedAmount: Number(form.firstDisbursedAmount || 0),
        otherSubsidyAmount: Number(form.otherSubsidyAmount || 0),
        otherSchemeUnitCost: Number(form.otherSchemeUnitCost || 0),
        documents,
        status: firstDisbursementRecord?.available ? "DETAILS_AND_DISBURSEMENT_RECORDED" : "DISBURSEMENT_PROOF_PENDING",
        workStartVerified: true,
        workStartDate: work.workStartDate,
        updatedAtIso: new Date().toISOString(),
        updatedByUid: user.uid,
      };
      const update = {
        subsidyFirstInstallment: payload,
        firstLoanDisbursedAmount: payload.firstDisbursedAmount,
        firstLoanDisbursementDate: form.firstDisbursementDate,
        subsidyFirstInstallmentStage: payload.status,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      onSaved?.(update);
      show("Subsidy First Instalment details saved successfully.", "success");
    } catch (saveError) {
      console.error(saveError);
      show(saveError.message || "Subsidy details could not be saved.", "error");
    } finally { setBusy(""); }
  };

  const uploadSelected = async () => {
    const user = auth.currentUser;
    if (!user || !project.id) return show("Application/login is unavailable.", "error");
    if (!work.started) return show("Documents can be uploaded only after work start is recorded.", "error");
    const selected = Object.entries(selectedFiles).filter(([, file]) => file);
    if (!selected.length) return show("Select at least one checklist document.", "error");
    try {
      setBusy("upload");
      const next = { ...documents };
      for (const [groupId, file] of selected) {
        if (file.size > MAX_FILE_SIZE) throw new Error(`${file.name} exceeds the 20 MB limit.`);
        if (
          groupId === SIGNED_APPLICATION.id &&
          file.type !== "application/pdf" &&
          !file.name.toLowerCase().endsWith(".pdf")
        )
          throw new Error(
            "The manually signed First Instalment Application must be uploaded as PDF.",
          );
        const version = Number(next[groupId]?.version || 0) + 1;
        const storagePath = `nafed_pacs/${user.uid}/${project.id}/subsidy_first_installment/source/${groupId}/v${version}_${Date.now()}_${cleanFileName(file.name)}`;
        const objectRef = ref(storage, storagePath);
        await uploadBytes(objectRef, file, { contentType: file.type || "application/octet-stream", customMetadata: { projectId: project.id, documentGroup: groupId, uploadedByUid: user.uid } });
        next[groupId] = {
          code: groupId,
          title:
            [SIGNED_APPLICATION, ...CHECKLIST].find(
              (item) => item.id === groupId,
            )?.title || groupId,
          fileName: file.name,
          storagePath,
          url: await getDownloadURL(objectRef),
          version,
          status: "UPLOADED",
          uploadedAtIso: new Date().toISOString(),
          uploadedByUid: user.uid,
        };
      }
      const previous = project.subsidyFirstInstallment || {};
      const payload = { ...previous, ...form, documents: next, status: "CHECKLIST_DOCUMENTS_UPDATED", updatedAtIso: new Date().toISOString(), updatedByUid: user.uid };
      const update = { subsidyFirstInstallment: payload, subsidyFirstInstallmentStage: payload.status, updatedAt: serverTimestamp() };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      setDocuments(next);
      setSelectedFiles({});
      onSaved?.(update);
      show("Subsidy checklist documents uploaded successfully.", "success");
    } catch (error) {
      console.error(error);
      show(error.message || "Subsidy documents could not be uploaded.", "error");
    } finally { setBusy(""); }
  };

  const generateForm = async (preview = false) => {
    const error = validateDetails();
    if (error) return show(error, "error");
    try {
      const html = buildSubsidyFirstInstallmentHtml(data);
      if (preview) return openOfficialPreview(html);
      setBusy("subsidy-form-pdf");
      await downloadOfficialPdf(
        html,
        `Subsidy_First_Instalment_Form_${safeFilePart(identity.societyName)}.pdf`,
      );
    } catch (generationError) { show(generationError.message, "error"); }
    finally { setBusy(""); }
  };

  const generateUndertaking = async (kind, preview = false) => {
    try {
      const html = buildSubsidyUndertakingHtml(kind, data);
      if (preview) return openOfficialPreview(html);
      setBusy(`undertaking-${kind}`);
      await downloadOfficialPdf(
        html,
        `${kind === "INSURANCE" ? "Godown_Insurance" : "No_Double_Subsidy"}_Undertaking_${safeFilePart(identity.societyName)}.pdf`,
      );
    } catch (error) { show(error.message, "error"); }
    finally { setBusy(""); }
  };

  const appendRemote = async (entries, folder, record, fallbackName) => {
    if (!record?.url || record.url === "MULTIPLE_RESOLUTIONS") return;
    entries.push({ name: `${folder}/${cleanFileName(record.fileName || fallbackName)}`, data: await fetchRemoteBlob(record.url, fallbackName) });
  };

  const generatePackage = async () => {
    const validation = validateDetails();
    if (validation) return show(validation, "error");
    if (!packageReady) return show(`Complete signed application/checklist: ${missingDocuments.map((item) => item.title).join(" · ")}`, "error");
    const user = auth.currentUser;
    if (!user) return show("Login session has expired.", "error");
    try {
      setBusy("package");
      const indexHtml = buildPackageIndexHtml({
        title: "Subsidy First Instalment File",
        project: identity,
        items: [
          {
            ...SIGNED_APPLICATION,
            record: signedApplicationRecord,
            available: Boolean(signedApplicationRecord?.url),
          },
          ...checklistState,
        ],
        notes: [
          "This file is generated only after PMC-confirmed work start and first bank-loan disbursement.",
          "The signed application is followed by the official ten-document checklist.",
        ],
      });
      const formHtml = buildSubsidyFirstInstallmentHtml(data);
      const noDoubleHtml = buildSubsidyUndertakingHtml("NO_DOUBLE_SUBSIDY", data);
      const insuranceHtml = buildSubsidyUndertakingHtml("INSURANCE", data);
      const entries = [
        {
          name: "00_Index_and_Checklist.pdf",
          data: await officialHtmlToPdfBlob(indexHtml, "Subsidy_Index.pdf"),
        },
        {
          name: "01_First_Instalment_Application_Gujarati.pdf",
          data: await officialHtmlToPdfBlob(
            formHtml,
            "Subsidy_First_Instalment_Application.pdf",
          ),
        },
        {
          name: "90_No_Double_Subsidy_Undertaking_Draft.pdf",
          data: await officialHtmlToPdfBlob(
            noDoubleHtml,
            "No_Double_Subsidy_Undertaking.pdf",
          ),
        },
        {
          name: "91_Godown_Insurance_Undertaking_Draft.pdf",
          data: await officialHtmlToPdfBlob(
            insuranceHtml,
            "Godown_Insurance_Undertaking.pdf",
          ),
        },
      ];
      await appendRemote(
        entries,
        "02_Signed_First_Instalment_Application",
        signedApplicationRecord,
        "Signed_First_Instalment_Application.pdf",
      );
      for (let index = 0; index < checklistState.length; index += 1) {
        const group = checklistState[index];
        const folder = `${String(index + 3).padStart(2, "0")}_${safeFilePart(group.id)}`;
        if (group.id === "RESOLUTION") {
          if (group.record?.url && group.record.url !== "MULTIPLE_RESOLUTIONS") {
            await appendRemote(
              entries,
              folder,
              group.record,
              "Five_Official_PACS_Documents_Combined.pdf",
            );
          } else {
            const officialDocuments = officialPacsDocuments(project);
            if (officialDocuments.length) {
              for (const item of officialDocuments)
                await appendRemote(
                  entries,
                  folder,
                  item,
                  item.fileName || `${item.type}.pdf`,
                );
            } else {
              await appendRemote(entries, folder, group.record, "Resolution.pdf");
            }
          }
        } else if (group.id === "LAND_LEASE") {
          const landFiles = ["SIGNED_LEASE", "LAND_RECORD"]
            .map((type) => project.landLeaseDocuments?.[type])
            .filter((item) => item?.url);
          if (landFiles.length) for (const item of landFiles) await appendRemote(entries, folder, item, item.fileName || `${item.type}.pdf`);
          else await appendRemote(entries, folder, group.record, "Land_or_Lease.pdf");
        } else if (group.id === "DPR_DRAWINGS") {
          for (const item of pmcTechnicalState.filter(
            (record) => record.requiredForSubsidy && record.available,
          ))
            await appendRemote(
              entries,
              folder,
              item.record,
              `${item.id}.pdf`,
            );
        } else await appendRemote(entries, folder, group.record, `${group.id}.pdf`);
      }
      for (const item of reusableFoundation.filter((record) => record.available))
        await appendRemote(
          entries,
          "20_Reused_Application_Foundation_Documents",
          item.record,
          `${item.id}.pdf`,
        );
      const registrarPackage = project.registrarPackage;
      if (registrarPackage?.url)
        await appendRemote(
          entries,
          "21_Previous_Registrar_File",
          registrarPackage,
          registrarPackage.fileName || "District_Registrar_File.zip",
        );
      entries.push({ name: "manifest.json", data: JSON.stringify({ packageType: "SUBSIDY_FIRST_INSTALLMENT", projectId: project.id, applicationId: identity.applicationId, pacsName: identity.societyName, workStartDate: work.workStartDate, firstDisbursedAmount: Number(form.firstDisbursedAmount), generatedAtIso: new Date().toISOString(), files: entries.map((entry) => entry.name) }, null, 2) });
      const zip = await createZipBlob(entries);
      const version = Number(project.subsidyFirstInstallment?.packageVersion || 0) + 1;
      const fileName = `Subsidy_First_Instalment_${safeFilePart(identity.district)}_${safeFilePart(identity.societyName)}_${safeFilePart(identity.applicationId)}_V${version}.zip`;
      const storagePath = `nafed_pacs/${user.uid}/${project.id}/subsidy_first_installment/packages/v${version}_${fileName}`;
      const objectRef = ref(storage, storagePath);
      await uploadBytes(objectRef, zip, { contentType: "application/zip", customMetadata: { projectId: project.id, packageVersion: String(version), generatedByUid: user.uid } });
      const packageRecord = { version, fileName, storagePath, url: await getDownloadURL(objectRef), status: "READY", fileCount: entries.length, generatedAtIso: new Date().toISOString(), generatedByUid: user.uid };
      const previous = project.subsidyFirstInstallment || {};
      const subsidy = { ...previous, ...form, documents, package: packageRecord, packageVersion: version, status: "SUBSIDY_FIRST_INSTALLMENT_FILE_READY", workStartVerified: true, workStartDate: work.workStartDate, updatedAtIso: packageRecord.generatedAtIso, updatedByUid: user.uid };
      const update = {
        subsidyFirstInstallment: subsidy,
        subsidyFirstInstallmentStage: subsidy.status,
        workflowStage: "SUBSIDY_FIRST_INSTALLMENT_FILE_READY",
        currentStage: "Subsidy First Instalment File Ready",
        subsidyDocumentReuse: {
          registrarPackageVersion: project.registrarPackageVersion || 0,
          foundationDocumentCount: reusableFoundation.filter(
            (item) => item.available,
          ).length,
          officialPacsDocumentCount: officialPacsDocuments(project).length,
          pmcTechnicalDocumentCount: pmcTechnicalState.filter(
            (item) => item.requiredForSubsidy && item.available,
          ).length,
          linkedAtIso: packageRecord.generatedAtIso,
        },
        subsidyPackageHistory: arrayUnion(packageRecord),
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), update);
      await setDoc(doc(db, "nafed_activity", `${project.id}_SUBSIDY_PACKAGE_${Date.now()}`), { action: "PACS_SUBSIDY_FIRST_INSTALLMENT_PACKAGE_GENERATED", entityType: "GODOWN_PROJECT", entityId: project.id, applicationId: identity.applicationId, packageVersion: version, fileCount: entries.length, firstDisbursedAmount: Number(form.firstDisbursedAmount), performedByUid: user.uid, performedByRole: "PACS", createdAt: serverTimestamp(), createdAtIso: packageRecord.generatedAtIso });
      downloadBlob(zip, fileName);
      onSaved?.(update);
      show("Subsidy First Instalment ZIP generated, saved and downloaded.", "success");
    } catch (error) {
      console.error(error);
      show(error.message || "Subsidy ZIP could not be generated.", "error");
    } finally { setBusy(""); }
  };

  if (!work.started) return <div className="subsidy-center"><style>{STYLE}</style><section className="subsidy-locked"><i>LOCKED</i><small>SUBSIDY · FIRST INSTALMENT</small><h2>Available after Work Start</h2><p>The module will unlock automatically when PMC records the work-start date/status in the Construction module.</p><div><b>Current work status</b><span>{work.status || "NOT STARTED"}</span></div></section></div>;

  return <div className="subsidy-center">
    <style>{STYLE}</style>
    <header className="subsidy-hero"><div><small>WORK START VERIFIED · FIRST INSTALMENT</small><h2>Godown Construction Subsidy File</h2><p>Signed Gujarati application, bank disbursement, undertakings and the exact 10-document checklist.</p></div><b className={packageReady ? "ready" : "pending"}>{packageReady ? "ZIP READY" : `${missingDocuments.length} ITEMS PENDING`}</b></header>
    {message.text && <div className={`subsidy-message ${message.type}`}>{message.text}<button onClick={() => setMessage({ type: "", text: "" })}>×</button></div>}
    {!stageUnlocked && <div className="stage-warning"><b>First Instalment gate is not complete</b>{stageReasons.map((reason) => <span key={reason}>• {reason}</span>)}</div>}
    <section className={signedApplicationRecord?.url ? "subsidy-card signed-application ready" : "subsidy-card signed-application pending"}><div><small>REQUIRED SIGNED FORM</small><h3>Manually Signed First Instalment Application PDF</h3><p>{signedApplicationRecord?.fileName || "Download the Gujarati form below, print it, add manual signatures and society seal, scan it as PDF, then upload it here."}</p></div>{signedApplicationRecord?.url && <a href={signedApplicationRecord.url} target="_blank" rel="noreferrer">View Signed PDF</a>}<label>Upload / Replace Signed PDF<input type="file" accept="application/pdf,.pdf" onChange={(event) => setSelectedFiles((old) => ({ ...old, [SIGNED_APPLICATION.id]: event.target.files?.[0] || null }))}/></label><button onClick={uploadSelected} disabled={Boolean(busy)}>{busy === "upload" ? "Uploading..." : "Upload Selected Signed Form"}</button></section>
    <section className="subsidy-card"><h3>1. Society and Project (Auto-filled)</h3><div className="auto-grid"><Info label="PACS / Society" value={identity.societyName}/><Info label="Registration" value={identity.registrationNumber}/><Info label="Project Location" value={`${identity.village}, ${identity.taluka}, ${identity.district}`}/><Info label="Capacity" value={`${identity.capacityMt || 0} MT`}/><Info label="Land" value={`${identity.landOwnershipType}${identity.landOwnershipType.includes("LEASE") ? ` · ${identity.leaseYears} years` : ""}`}/><Info label="Work Started" value={work.workStartDate || "Recorded by PMC"}/></div></section>
    <section className="subsidy-card reused-card"><h3>2. Automatically Reused Application &amp; Registrar Documents</h3><p>These files are linked from the original application/Registrar cycle; PACS does not upload them again.</p><div className="reuse-list">{reusableFoundation.map((item) => <article className={item.available ? "available" : "missing"} key={item.id}><b>{item.title}</b><span>{item.available ? item.record.fileName || "Linked document" : "Original application document not found"}</span><strong>{item.available ? "LINKED" : "PENDING"}</strong>{item.record?.url && <a href={item.record.url} target="_blank" rel="noreferrer">View</a>}</article>)}</div></section>
    <section className="subsidy-card reused-card"><h3>3. PMC Technical File (Automatic after Work Start)</h3><p>Survey, Soil Test, Design, Final Estimate and DPR are uploaded only by the allocated PMC and become available here automatically.</p><div className="reuse-list technical-reuse">{pmcTechnicalState.filter((item) => item.requiredForSubsidy).map((item) => <article className={item.available ? "available" : "missing"} key={item.id}><b>{item.title}</b><span>{item.available ? `Version ${item.record.version || 1} · ${item.record.fileName || "PMC document"}` : "Waiting for PMC upload"}</span><strong>{item.available ? "LINKED" : "PMC PENDING"}</strong>{item.record?.url && <a href={item.record.url} target="_blank" rel="noreferrer">View</a>}</article>)}</div></section>
    <section className="subsidy-card"><h3>4. Bank Loan and First Disbursement</h3><div className="subsidy-grid"><Field label="Scheme Financial Year" value={form.schemeYear} onChange={(value) => set("schemeYear", value)}/><Field label="Executing Agency / Contractor" value={form.executingAgency} onChange={(value) => set("executingAgency", value)}/><Field label="Loan Sanction Letter Number" value={form.loanSanctionNumber} onChange={(value) => set("loanSanctionNumber", value)}/><Field label="Loan Sanction Date" type="date" value={form.loanSanctionDate} onChange={(value) => set("loanSanctionDate", value)}/><Field label="Total Sanctioned Loan (₹)" type="number" value={form.totalSanctionedLoan} onChange={(value) => set("totalSanctionedLoan", value)}/><Field label="First Disbursed Amount (₹)" type="number" value={form.firstDisbursedAmount} onChange={(value) => set("firstDisbursedAmount", value)}/><Field label="First Disbursement Date" type="date" value={form.firstDisbursementDate} onChange={(value) => set("firstDisbursementDate", value)}/><Field label="Loan / Cash Credit Account Number" value={form.loanAccountNumber} onChange={(value) => set("loanAccountNumber", value)}/><Select label="Other Central Government Scheme Benefit" value={form.otherCentralSchemeBenefit} onChange={(value) => set("otherCentralSchemeBenefit", value)} options={["NO", "YES"]}/>{form.otherCentralSchemeBenefit === "YES" && <><Field label="Other Scheme Name" value={form.otherSchemeName} onChange={(value) => set("otherSchemeName", value)}/><Field label="Other Subsidy Amount (₹)" type="number" value={form.otherSubsidyAmount} onChange={(value) => set("otherSubsidyAmount", value)}/><Field label="Other Scheme Unit Cost (₹)" type="number" value={form.otherSchemeUnitCost} onChange={(value) => set("otherSchemeUnitCost", value)}/></>}</div><div className="form-actions"><button onClick={saveDetails} disabled={Boolean(busy)}>{busy === "save" ? "Saving..." : "Save First Instalment Details"}</button><button onClick={() => generateForm(false)}>Download Gujarati PDF</button><button onClick={() => generateForm(true)}>Preview / Print PDF</button></div></section>
    <section className="subsidy-card"><h3>5. Mandatory Checklist</h3><div className="subsidy-list">{checklistState.map((group, index) => <article className={group.available ? "available" : "missing"} key={group.id}><i>{String(index + 1).padStart(2, "0")}</i><div><b>{group.title}</b><span>{group.available ? group.record?.title || group.record?.fileName || "Automatically linked from project" : group.id === "DPR_DRAWINGS" ? "Waiting for all five PMC technical documents" : "Required document pending"}</span></div><strong>{group.available ? "READY" : group.id === "DPR_DRAWINGS" ? "PMC PENDING" : "PENDING"}</strong>{group.record?.url && !["MULTIPLE_RESOLUTIONS", "MULTIPLE_PMC_TECHNICAL"].includes(group.record.url) && <a href={group.record.url} target="_blank" rel="noreferrer">View</a>}{group.id === "DPR_DRAWINGS" ? <em>PMC CONTROLLED</em> : <label>Upload / Replace<input type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" onChange={(event) => setSelectedFiles((old) => ({ ...old, [group.id]: event.target.files?.[0] || null }))}/></label>}</article>)}</div><footer><div><button onClick={() => generateUndertaking("NO_DOUBLE_SUBSIDY")}>No-Double-Subsidy PDF</button><button onClick={() => generateUndertaking("INSURANCE")}>Insurance Undertaking PDF</button></div><button className="primary" onClick={uploadSelected} disabled={Boolean(busy)}>{busy === "upload" ? "Uploading..." : "Upload Selected Checklist Documents"}</button></footer></section>
    <section className="subsidy-card final-package"><div><small>FINAL OUTPUT</small><h3>Subsidy First Instalment ZIP</h3><p>Available only after work start, first bank disbursement and all signed checklist documents.</p></div>{project.subsidyFirstInstallment?.package?.url && <a href={project.subsidyFirstInstallment.package.url} target="_blank" rel="noreferrer">Download Last Saved ZIP</a>}<button onClick={generatePackage} disabled={Boolean(busy) || !packageReady}>{busy === "package" ? "Building Complete ZIP..." : "Generate & Download Subsidy ZIP"}</button></section>
  </div>;
}

function Field({ label, value, onChange, type = "text" }) { return <label className="subsidy-field"><span>{label}</span><input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)}/></label>; }
function Select({ label, value, onChange, options }) { return <label className="subsidy-field"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option value={option} key={option}>{option}</option>)}</select></label>; }
function Info({ label, value }) { return <div className="subsidy-info"><span>{label}</span><b>{firstText(value, "Not available")}</b></div>; }

const STYLE = `
.subsidy-center{display:grid;gap:18px;color:#17233a}.subsidy-locked{max-width:760px;margin:35px auto;background:white;border:1px solid #dbe4ed;border-radius:20px;padding:42px;text-align:center;box-shadow:0 18px 44px #183d6515}.subsidy-locked i{display:inline-block;border-radius:999px;background:#fff0d1;color:#875700;font-style:normal;font-weight:900;padding:8px 13px}.subsidy-locked small{display:block;margin-top:18px;color:#4d6a87;font-weight:900;letter-spacing:.1em}.subsidy-locked h2{font-size:31px;margin:7px}.subsidy-locked p{color:#637489}.subsidy-locked div{display:flex;justify-content:space-between;border-top:1px solid #e1e7ee;margin-top:22px;padding-top:16px}.subsidy-hero{display:flex;justify-content:space-between;align-items:center;background:linear-gradient(135deg,#17385f,#08739c);color:white;border-radius:18px;padding:25px 28px}.subsidy-hero small{font-weight:900;letter-spacing:.1em;color:#bfe8f6}.subsidy-hero h2{font-size:29px;margin:6px 0}.subsidy-hero p{margin:0;color:#e0f2f7}.subsidy-hero>b{border-radius:999px;padding:10px 14px;font-size:11px}.subsidy-hero>b.ready{background:#dcf8e7;color:#116d3d}.subsidy-hero>b.pending{background:#fff0cf;color:#845500}.subsidy-message,.stage-warning{display:flex;gap:8px;border-radius:12px;padding:13px 16px;font-weight:800}.subsidy-message{justify-content:space-between}.subsidy-message.success{background:#e7f8ee;color:#157240}.subsidy-message.error{background:#fff0ef;color:#a22d24}.subsidy-message button{border:0;background:none;font-size:20px}.stage-warning{flex-direction:column;background:#fff7e6;border-left:4px solid #d89013;color:#734900}.subsidy-card{background:white;border:1px solid #dbe5ee;border-radius:16px;padding:20px}.subsidy-card h3{margin:0 0 15px;color:#0b3d66}.reused-card>p{margin:-8px 0 13px;color:#64758a;font-size:11px}.reuse-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.reuse-list article{display:grid;grid-template-columns:1fr auto;gap:4px 10px;padding:11px;border:1px solid #d8e2ec;border-radius:9px}.reuse-list article.available{border-left:4px solid #15925a;background:#f5fbf7}.reuse-list article.missing{border-left:4px solid #d59118;background:#fffaf0}.reuse-list b{font-size:11px}.reuse-list span{grid-column:1;color:#687a8f;font-size:9px}.reuse-list strong{grid-column:2;grid-row:1;font-size:8px}.reuse-list a{grid-column:2;grid-row:2;color:#0a6ba4;font-size:9px;font-weight:900}.auto-grid,.subsidy-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.subsidy-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.subsidy-info{border:1px solid #dbe4ed;background:#f8fbfe;border-radius:11px;padding:12px;display:grid;gap:4px}.subsidy-info span,.subsidy-field span{font-size:11px;color:#61758b;font-weight:800}.subsidy-field{display:grid;gap:6px}.subsidy-field input,.subsidy-field select{border:1px solid #c8d5e1;border-radius:10px;padding:11px 12px;background:#fbfdff;font:inherit}.form-actions{display:flex;justify-content:flex-end;gap:9px;border-top:1px solid #e3e9ef;margin-top:16px;padding-top:15px}.form-actions button,.subsidy-card footer button,.final-package button,.final-package a{border:1px solid #0c679c;background:white;color:#0b5989;border-radius:10px;padding:10px 13px;font-weight:800;text-decoration:none}.subsidy-list{display:grid;gap:9px}.subsidy-list article{display:grid;grid-template-columns:40px 1fr auto auto minmax(120px,180px);gap:11px;align-items:center;border:1px solid #d9e3ed;border-radius:12px;padding:11px}.subsidy-list article.available{border-left:4px solid #15925a}.subsidy-list article.missing{border-left:4px solid #d59118}.subsidy-list i{font-style:normal;font-weight:900;color:#0c5e8e}.subsidy-list article div{display:grid;gap:3px}.subsidy-list span{font-size:11px;color:#687a8f}.subsidy-list strong{font-size:10px}.subsidy-list a{color:#0a6ba4;font-weight:800}.subsidy-list label{font-size:10px;font-weight:800;color:#5b7088}.subsidy-list input{display:block;max-width:175px;margin-top:3px}.subsidy-list em{padding:7px;border-radius:5px;background:#eef4fa;color:#526a82;font-size:8px;font-style:normal;font-weight:900;text-align:center}.subsidy-card footer{display:flex;justify-content:space-between;gap:10px;border-top:1px solid #e3e9ef;margin-top:15px;padding-top:15px}.subsidy-card footer div{display:flex;gap:8px}.subsidy-card footer button.primary{background:#0879b8;color:white}.final-package{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:14px}.final-package small{font-weight:900;color:#63809a}.final-package h3{margin:3px 0}.final-package p{margin:0;color:#68798b}.final-package button{background:#0879b8;color:white}.final-package button:disabled,.form-actions button:disabled,.subsidy-card footer button:disabled{opacity:.5;cursor:not-allowed}@media(max-width:900px){.subsidy-hero{align-items:flex-start;flex-direction:column;gap:14px}.auto-grid,.subsidy-grid,.reuse-list{grid-template-columns:1fr}.subsidy-list article{grid-template-columns:36px 1fr}.subsidy-list article>*:nth-child(n+3){grid-column:2}.subsidy-card footer,.form-actions{flex-direction:column}.subsidy-card footer div{flex-direction:column}.final-package{grid-template-columns:1fr}.final-package>*{width:100%;text-align:center}}
.signed-application{display:grid;grid-template-columns:1fr auto minmax(190px,260px) auto;gap:12px;align-items:center;border-left:5px solid #d59118}.signed-application.ready{border-left-color:#15925a;background:#f7fcf9}.signed-application small{color:#55758f;font-size:9px;font-weight:900;letter-spacing:.08em}.signed-application h3{margin:3px 0}.signed-application p{margin:0;color:#65788d;font-size:10px}.signed-application a{color:#0a6ba4;font-weight:900}.signed-application label{font-size:9px;font-weight:900;color:#536d84}.signed-application input{display:block;max-width:250px;margin-top:4px}.signed-application button{border:0;border-radius:9px;padding:10px 13px;background:#0879b8;color:#fff;font-weight:900}.signed-application button:disabled{opacity:.5}@media(max-width:900px){.signed-application{grid-template-columns:1fr}.signed-application>*{width:100%}}
`;
